/**
 * Vue自定义指令 v-el-validate
 * 用于自动处理 Element UI 组件的表单验证
 * 
 * 使用方法：
 * 1. 在 el-select 或 el-input 上添加 v-el-validate
 * 2. 在组件 data 中添加 submitted: false
 * 3. 在提交时设置 submitted = true 触发验证显示
 * 
 * 示例：
 * <el-select v-el-validate v-model="field" placeholder="请选择">
 *   ...
 * </el-select>
 */

export default {
  // 指令绑定到元素时
  bind(el, binding, vnode) {
    const vm = vnode.context;
    const fieldName = getFieldName(el, vnode);
    
    if (!fieldName) {
      console.warn('v-el-validate: 无法获取字段名');
      return;
    }

    // 创建错误提示元素
    const errorDiv = document.createElement('div');
    errorDiv.className = 'el-validate-error-text';
    errorDiv.style.cssText = `
      color: #F56C6C;
      font-size: 12px;
      line-height: 1;
      padding-top: 4px;
      position: absolute;
      bottom: 2px;
      left: 10px;
      display: none;
    `;
    
    // 获取placeholder作为错误提示
    const placeholder = el.querySelector('input')?.placeholder || '此项';
    errorDiv.textContent = `请填写${placeholder.replace('请输入', '').replace('请选择', '')}`;
    
    // 将错误提示添加到td中
    const td = el.closest('td');
    if (td) {
      td.style.position = 'relative';
      td.style.paddingBottom = '20px';
      td.appendChild(errorDiv);
    }

    // 存储引用以便后续使用
    el._elValidate = {
      errorDiv,
      fieldName,
      vm
    };

    // 监听值变化
    const unwatch = vm.$watch(fieldName, (newVal) => {
      updateValidation(el, newVal, vm.submitted);
    }, { deep: true });

    // 也监听 submitted 变化
    const unwatchSubmitted = vm.$watch('submitted', (submitted) => {
      const value = getValueByPath(vm, fieldName);
      updateValidation(el, value, submitted);
    });

    el._elValidate.unwatch = unwatch;
    el._elValidate.unwatchSubmitted = unwatchSubmitted;

    // 初始验证
    const initialValue = getValueByPath(vm, fieldName);
    updateValidation(el, initialValue, vm.submitted);
  },

  // 元素更新时
  update(el, binding, vnode) {
    const vm = vnode.context;
    const fieldName = el._elValidate?.fieldName;
    
    if (fieldName && vm) {
      const value = getValueByPath(vm, fieldName);
      updateValidation(el, value, vm.submitted);
    }
  },

  // 指令解绑时清理
  unbind(el) {
    if (el._elValidate) {
      el._elValidate.unwatch && el._elValidate.unwatch();
      el._elValidate.unwatchSubmitted && el._elValidate.unwatchSubmitted();
      
      if (el._elValidate.errorDiv && el._elValidate.errorDiv.parentNode) {
        el._elValidate.errorDiv.parentNode.removeChild(el._elValidate.errorDiv);
      }
      
      delete el._elValidate;
    }
  }
};

/**
 * 更新验证状态
 */
function updateValidation(el, value, submitted) {
  const isEmpty = !value || (Array.isArray(value) && value.length === 0);
  const shouldShowError = submitted && isEmpty;
  
  // 添加/移除错误样式
  if (shouldShowError) {
    el.classList.add('is-error');
  } else {
    el.classList.remove('is-error');
  }
  
  // 显示/隐藏错误提示
  if (el._elValidate && el._elValidate.errorDiv) {
    el._elValidate.errorDiv.style.display = shouldShowError ? 'block' : 'none';
  }
}

/**
 * 从vnode中获取字段名
 */
function getFieldName(el, vnode) {
  // 尝试从v-model获取
  const data = vnode.data || {};
  const model = data.model;
  
  if (model && model.expression) {
    return model.expression;
  }
  
  // 尝试从组件实例获取
  const componentInstance = vnode.componentInstance;
  if (componentInstance && componentInstance.$vnode && componentInstance.$vnode.data) {
    const compModel = componentInstance.$vnode.data.model;
    if (compModel && compModel.expression) {
      return compModel.expression;
    }
  }
  
  return null;
}

/**
 * 根据路径获取对象值
 */
function getValueByPath(obj, path) {
  if (!path) return undefined;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    
    // 处理数组索引，如 item.name 或 basic_data.field
    if (key.includes('[')) {
      const [arrName, indexStr] = key.split('[');
      const index = parseInt(indexStr.replace(']', ''));
      value = value[arrName];
      if (Array.isArray(value) && index < value.length) {
        value = value[index];
      } else {
        return undefined;
      }
    } else {
      value = value[key];
    }
  }
  
  return value;
}
